import { useId, useState, type FormEvent } from 'react';
import type { TwoFactorSetupDto } from '@ecom/contracts';
import { useT } from '@/i18n/use-t';
import { Button, CopyButton, Field, Input } from './ui';

/**
 * The pieces of two-step sign-in (ADR-116) shared by the sign-in page and the
 * Sign-in security page: the QR code to scan, a code box, and the recovery
 * codes shown once.
 */

/** The secret as a QR code, and as text for an app that cannot scan. */
export function ScanCode({ setup }: { setup: TwoFactorSetupDto }) {
  const t = useT();
  return (
    <div className="flex flex-col items-center gap-3 text-center">
      <p className="text-sm text-ink-muted">{t('twoFactorScan')}</p>
      {/* Dark modules on white whatever the theme: a camera needs the contrast. */}
      <img
        src={`data:image/svg+xml;charset=utf-8,${encodeURIComponent(setup.qrSvg)}`}
        alt={t('twoFactorQrAlt')}
        className="size-44 rounded-control bg-white p-2"
      />
      <div className="flex w-full flex-col gap-1">
        <span className="text-xs text-ink-muted">{t('twoFactorKeyHint')}</span>
        <div className="flex items-center gap-2">
          <code dir="ltr" className="min-w-0 flex-1 break-all rounded-control bg-surface-sunken px-2 py-1.5 font-mono text-xs text-ink">
            {setup.secret.replace(/(.{4})/g, '$1 ').trim()}
          </code>
          <CopyButton value={setup.secret} label={t('copy')} copiedLabel={t('copied')} />
        </div>
      </div>
    </div>
  );
}

/** Six digits, or a recovery code where it is allowed. */
export function CodeForm({
  submit,
  submitting,
  label,
  action,
  recovery = false,
}: {
  submit: (code: string) => Promise<unknown>;
  submitting: boolean;
  label: string;
  action: string;
  recovery?: boolean;
}) {
  const t = useT();
  const id = useId();
  const [code, setCode] = useState('');

  async function handle(event: FormEvent) {
    event.preventDefault();
    await submit(code.trim());
    setCode('');
  }

  return (
    <form onSubmit={handle} className="flex flex-col gap-3">
      <Field label={label} htmlFor={id} hint={recovery ? t('twoFactorRecoveryHint') : undefined}>
        <Input
          id={id}
          dir="ltr"
          value={code}
          onChange={(event) => setCode(event.target.value)}
          // The phone's keyboard offers the code from a text; an app's code is typed.
          autoComplete="one-time-code"
          inputMode={recovery ? 'text' : 'numeric'}
          maxLength={recovery ? 20 : 6}
          placeholder="123456"
          className="text-center font-mono text-lg tracking-[0.3em]"
          required
          autoFocus
        />
      </Field>
      <Button type="submit" disabled={submitting || code.trim().length < 6}>
        {action}
      </Button>
    </form>
  );
}

/** Shown once: each works a single time in place of a code. */
export function RecoveryCodes({ codes }: { codes: string[] }) {
  const t = useT();
  const text = codes.join('\n');
  return (
    <div className="flex flex-col gap-3">
      <p className="text-sm text-ink-muted">{t('twoFactorCodesHint')}</p>
      <ol dir="ltr" className="grid grid-cols-2 gap-x-4 gap-y-1 rounded-control bg-surface-sunken p-3 font-mono text-sm text-ink">
        {codes.map((code) => (
          <li key={code}>{code}</li>
        ))}
      </ol>
      <div className="flex flex-wrap gap-2">
        <CopyButton value={text} label={t('twoFactorCopyCodes')} copiedLabel={t('copied')} />
        <Button
          variant="secondary"
          size="sm"
          onClick={() => {
            const link = document.createElement('a');
            link.href = URL.createObjectURL(new Blob([`${text}\n`], { type: 'text/plain' }));
            link.download = 'recovery-codes.txt';
            link.click();
            URL.revokeObjectURL(link.href);
          }}
        >
          {t('twoFactorDownloadCodes')}
        </Button>
      </div>
    </div>
  );
}
